/**
 * Raft tRPC Client
 *
 * Lightweight, fully-typed fetch client for calling tRPC endpoints on the Raft
 * server. Types are inferred directly from the AppRouter — procedure paths,
 * inputs, and outputs are all statically checked.
 *
 * @example
 * // Mutation — path, input, and return type are all inferred
 * const result = await raft.mutate("blockedWords.remove", { wordId: "uuid" });
 *
 * // Query
 * const data = await raft.query("users.get", { id: 1 });
 */

import type { inferRouterInputs, inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@brave-new-world/pictufy/src/trpc/router.js";

// ── Type helpers ─────────────────────────────────────────────────

type RouterInputs = inferRouterInputs<AppRouter>;
type RouterOutputs = inferRouterOutputs<AppRouter>;

/** All dot-separated procedure paths on the AppRouter */
export type ProcedurePath = {
  [K in keyof RouterInputs]: {
    [P in keyof RouterInputs[K]]: `${K & string}.${P & string}`;
  }[keyof RouterInputs[K]];
}[keyof RouterInputs];

/** Extract the input type for a router procedure */
export type InputFor<T extends ProcedurePath> = T extends `${infer NS}.${infer P}`
  ? NS extends keyof RouterInputs
    ? P extends keyof RouterInputs[NS]
      ? RouterInputs[NS][P]
      : never
    : never
  : never;

/** Extract the output type for a router procedure */
export type OutputFor<T extends ProcedurePath> = T extends `${infer NS}.${infer P}`
  ? NS extends keyof RouterOutputs
    ? P extends keyof RouterOutputs[NS]
      ? RouterOutputs[NS][P]
      : never
    : never
  : never;

// ── Config ───────────────────────────────────────────────────────

/**
 * Base URL of the pictufy server.
 *
 * Same-origin, with no exceptions: the server renders the page AND serves
 * /trpc, so whatever host the document came from is the host to call. That keeps
 * this bundle portable across dev / staging / production with no rebuild — the
 * environment is decided by DNS, not by a compiled-in hostname.
 *
 * This used to special-case `localhost` to `http://localhost:4001`, from the era
 * when vite dev-served the page on its own port. Local dev has not worked that
 * way for a long time: `STATIC_BASE_URL=""` puts the pages, the bundle and /trpc
 * on one Express server, and `root.env` pins `PORT=4000` (only `env.ts`'s unused
 * default is 4001). So every tRPC call from the browser hit a refused connection
 * on localhost — the local feedback loop the mode exists to provide was dead,
 * and a browser test is what surfaced it.
 */
export const SERVER_BASE_URL = window.location.origin;

// ── Internal helpers ─────────────────────────────────────────────
//
// Wire format: the raft tRPC server runs WITHOUT a data transformer
// (`initTRPC.create()` in apps/pictufy/src/trpc/trpc.ts). For that configuration
// the input is sent RAW — query as `?input=<json>`, mutation as the JSON body —
// and the response is `{ result: { data } }` / `{ error }`. (A `{ json: input }`
// envelope is only correct when the server is configured with a transformer.)
// Keep this in sync with the server's transformer setting.

interface TrpcResult<T = unknown> {
  result: { data: T };
}

interface TrpcError {
  error: {
    message: string;
    code: number;
    data: { code: string; httpStatus: number };
  };
}

function isTrpcError(body: unknown): body is TrpcError {
  return typeof body === "object" && body !== null && "error" in body;
}

function isTrpcResult(body: unknown): body is TrpcResult {
  return typeof body === "object" && body !== null && "result" in body;
}

/**
 * Turn a `/trpc` response into its `result.data`, or throw something readable.
 *
 * Both callers used to `await response.json()` *before* checking `response.ok`,
 * so any non-JSON failure — an ALB 502 page, an nginx error, a redirect to an
 * HTML login page — rejected with `SyntaxError: Unexpected token '<'` instead of
 * the `Raft request failed: 502` the next line was ready to throw. The status
 * check has to come first, and the parse has to be allowed to fail.
 *
 * The `result` check is the other half: the wire shape depends on the server
 * running WITHOUT a data transformer (see the note above). If one is ever added,
 * this throws at the seam naming the problem, rather than handing back
 * `undefined` to be dereferenced somewhere deep in a component.
 */
async function readTrpcResponse<T>(response: Response, path: string): Promise<T> {
  let body: unknown;
  let parseFailed = false;
  try {
    body = await response.json();
  } catch {
    parseFailed = true;
  }

  if (isTrpcError(body)) throw new Error(body.error.message);
  if (!response.ok) throw new Error(`Raft request failed: ${response.status} (${path})`);
  if (parseFailed) throw new Error(`Raft returned a non-JSON body (${path})`);
  if (!isTrpcResult(body)) throw new Error(`Raft returned an unexpected envelope (${path})`);

  return (body as TrpcResult<T>).result.data;
}

// ── Public API ───────────────────────────────────────────────────

/**
 * Call a tRPC mutation endpoint on the Raft server.
 * Path, input, and return type are all statically checked against AppRouter.
 */
async function mutate<T extends ProcedurePath>(path: T, input: InputFor<T>): Promise<OutputFor<T>> {
  const url = `${SERVER_BASE_URL}/trpc/${path}`;
  const response = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });

  return readTrpcResponse<OutputFor<T>>(response, path);
}

/**
 * Call a tRPC query endpoint on the Raft server.
 * Path, input, and return type are all statically checked against AppRouter.
 */
async function query<T extends ProcedurePath>(path: T, input: InputFor<T>): Promise<OutputFor<T>> {
  // Omit the param entirely for zero-arg procedures. `JSON.stringify(undefined)`
  // returns the value undefined, which a template literal coerces to the string
  // "undefined" — so every no-input query used to request `?input=undefined`.
  // Harmless while the procedure declares no `.input()` (tRPC then never reads
  // the param), but it would 400 the moment one gained an optional parser.
  const qs = input === undefined ? "" : `?input=${encodeURIComponent(JSON.stringify(input))}`;
  const url = `${SERVER_BASE_URL}/trpc/${path}${qs}`;

  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
  });

  return readTrpcResponse<OutputFor<T>>(response, path);
}

export const raft = { mutate, query };
